import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Sparkles, Info } from "lucide-react";
import { cmsApi, apiError, mediaUrl } from "../api";
import { ImageUpload } from "../components/ImageUpload";
import { usePageData, PageTitle, Card, TextField, SaveBar, Spinner } from "../components/admin-ui";

function LogoPreview({ name, logoId, dark }) {
  return (
    <div className={`rounded-xl px-5 py-4 flex items-center gap-3 border ${dark ? "bg-[#2d1015] border-white/10" : "bg-[#FAF7F2] border-stone-200"}`} data-testid={`logo-preview-${dark ? "dark" : "light"}`}>
      {logoId ? <img src={mediaUrl(logoId)} alt={name} className="h-10 w-auto max-w-[160px] object-contain" /> : <Sparkles className="h-5 w-5 text-[#C59B6C]" />}
      <span className={`font-serif text-xl tracking-[0.14em] uppercase ${dark ? "text-stone-50" : "text-[#4A1D24]"}`}>{name || "Hair By Samira"}</span>
    </div>
  );
}

export default function BrandingAdmin() {
  const { data, loading, reload } = usePageData("/website-admin/branding");
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (data) setForm((f) => f || { name: data.name, logo_media_id: data.logo_media_id, favicon_media_id: data.favicon_media_id, primary_color: data.primary_color, accent_color: data.accent_color }); }, [data]);
  if (loading || !form) return <Spinner />;
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));
  const dirtyLogo = form.logo_media_id !== data.logo_media_id;
  const save = async () => {
    if (!form.name.trim()) { toast.error("Bitte einen Namen angeben"); return; }
    setSaving(true);
    try { await cmsApi.put("/website-admin/branding", form); toast.success("Branding gespeichert"); reload(); }
    catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };

  return (
    <div data-testid="branding-admin-page">
      <PageTitle title="Logo & Branding" text="Logo, Name, Favicon und Markenfarben – erscheinen auf der gesamten Website." />
      <div className="grid lg:grid-cols-12 gap-6">
        <div className="lg:col-span-7 space-y-6">
          <Card title="Logo" text="Transparentes PNG oder WEBP empfohlen (Querformat, mind. 600 px breit). Ohne Logo wird der Name als elegantes Text-Logo angezeigt.">
            <ImageUpload value={form.logo_media_id} onChange={set("logo_media_id")} kind="logo" label="" aspect="aspect-[3/1]" testid="branding-logo" />
            {dirtyLogo && <p className="inline-flex items-center gap-1.5 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mt-3" data-testid="branding-logo-unsaved"><Info className="h-3.5 w-3.5" /> Vorschau – das Logo wird erst mit „Branding speichern“ auf der Website übernommen.</p>}
            {!form.logo_media_id && <p className="text-xs text-stone-500 mt-3" data-testid="branding-logo-fallback-hint">Aktuell: Text-Logo „{form.name}“ (kein Logo hochgeladen).</p>}
          </Card>
          <Card title="Vorschau" text="So erscheint Ihr Logo in Kopfzeile (hell) und Fußzeile (dunkel).">
            <div className="grid sm:grid-cols-2 gap-3">
              <LogoPreview name={form.name} logoId={form.logo_media_id} />
              <LogoPreview name={form.name} logoId={form.logo_media_id} dark />
            </div>
          </Card>
        </div>
        <div className="lg:col-span-5 space-y-6">
          <Card title="Name des Geschäfts" text="Wird als Text-Logo, in Überschriften und im Footer verwendet.">
            <TextField label="Name" value={form.name} onChange={set("name")} testid="branding-name-input" />
          </Card>
          <Card title="Favicon" text="Kleines quadratisches Symbol für den Browser-Tab (PNG, mind. 64×64 px).">
            <ImageUpload value={form.favicon_media_id} onChange={set("favicon_media_id")} kind="favicon" label="" aspect="aspect-square w-24" testid="branding-favicon" />
          </Card>
          <Card title="Markenfarben" text="Hauptfarbe für Flächen & Überschriften, Akzentfarbe für Details.">
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-[11px] uppercase tracking-wide text-stone-500">Hauptfarbe</label><div className="flex items-center gap-2 mt-1"><input type="color" value={form.primary_color} onChange={(e) => set("primary_color")(e.target.value)} className="h-9 w-12 rounded-md border" data-testid="branding-primary-color" /><span className="text-xs font-mono text-stone-500">{form.primary_color}</span></div></div>
              <div><label className="text-[11px] uppercase tracking-wide text-stone-500">Akzentfarbe</label><div className="flex items-center gap-2 mt-1"><input type="color" value={form.accent_color} onChange={(e) => set("accent_color")(e.target.value)} className="h-9 w-12 rounded-md border" data-testid="branding-accent-color" /><span className="text-xs font-mono text-stone-500">{form.accent_color}</span></div></div>
            </div>
            <div className="mt-4 rounded-xl p-4 text-stone-50" style={{ background: form.primary_color }} data-testid="branding-color-preview">
              <p className="text-[10px] uppercase tracking-[0.3em]" style={{ color: form.accent_color }}>Vorschau</p>
              <p className="font-serif text-xl mt-1">{form.name}</p>
              <span className="inline-block mt-3 px-4 py-1.5 rounded-full text-xs font-semibold text-[#2d1015]" style={{ background: form.accent_color }}>Button</span>
            </div>
          </Card>
        </div>
      </div>
      <SaveBar onSave={save} saving={saving} label="Branding speichern" testid="branding-save" />
    </div>
  );
}
